/**
 * Configuration and result helpers shared by the worker and the main-thread fallback.
 *
 * This module deliberately contains **no value import of `@mediapipe/tasks-vision`** so it
 * can be pulled into the main bundle for free; the ~140 kB runtime lives in
 * `createHandLandmarker.ts`, which the main thread only loads if it has to run inference
 * itself.
 */

import type { HandLandmarkerResult } from '@mediapipe/tasks-vision';
import type { Handedness } from './types';

export interface LandmarkerConfig {
  /**
   * Directory holding `vision_wasm_internal.*`, **without** a trailing slash — MediaPipe
   * concatenates `${base}/${file}` itself, and a double slash 404s on strict static servers.
   */
  wasmBasePath: string;
  /** Absolute URL of `hand_landmarker.task`. */
  modelAssetPath: string;
  numHands: number;
  minHandDetectionConfidence: number;
  minHandPresenceConfidence: number;
  minTrackingConfidence: number;
  delegate: 'GPU' | 'CPU';
}

export const DEFAULT_LANDMARKER_TUNING = {
  numHands: 2,
  minHandDetectionConfidence: 0.55,
  minHandPresenceConfidence: 0.55,
  minTrackingConfidence: 0.5,
} as const;

export type LandmarkerFailure = 'wasm-init' | 'model-download';

export class LandmarkerSetupError extends Error {
  readonly failure: LandmarkerFailure;

  constructor(failure: LandmarkerFailure, message: string, options?: { cause?: unknown }) {
    super(message, options);
    this.name = 'LandmarkerSetupError';
    this.failure = failure;
  }
}

export function classifyCreateFailure(error: unknown, modelPath: string): LandmarkerSetupError {
  const text = error instanceof Error ? error.message : String(error);
  const looksLikeDownload = /fetch|network|404|not found|failed to load|http/i.test(text);
  if (looksLikeDownload) {
    return new LandmarkerSetupError(
      'model-download',
      `The hand-tracking model could not be loaded from ${modelPath}. Run "npm run assets" to fetch it.`,
      { cause: error },
    );
  }
  return new LandmarkerSetupError(
    'wasm-init',
    `The hand-tracking model failed to initialise: ${text}`,
    { cause: error },
  );
}

/** MediaPipe reports handedness as a category name; normalise it. */
export function readHandedness(result: HandLandmarkerResult, index: number): Handedness {
  const categories = result.handednesses[index] ?? result.handedness[index];
  const name = categories?.[0]?.categoryName ?? '';
  if (name === 'Left') return 'Left';
  if (name === 'Right') return 'Right';
  return 'Unknown';
}

export function readHandednessScore(result: HandLandmarkerResult, index: number): number {
  const categories = result.handednesses[index] ?? result.handedness[index];
  return categories?.[0]?.score ?? 0;
}
